import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useFolder } from "src/core/contexts/FolderContext";
import {
  CloseMissionFormValues,
  closeMissionSchema,
} from "src/core/forms/missions/closeMission.schema";
import { useCloseMissionMutation } from "src/core/gql/graphql";
import { IMission } from "src/core/hooks/folder/useFolderMissions";
import { useToast } from "src/core/hooks/use-toast";

export const useCloseMission = (
  mission: IMission | null,
  setOpen: (open: boolean) => void,
  setMissionToEdit: (mission: IMission | null) => void
) => {
  const [closeMission, { loading }] = useCloseMissionMutation();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const { refetch } = useFolder();

  const form = useForm<CloseMissionFormValues>({
    resolver: zodResolver(closeMissionSchema(t)),
    mode: "onSubmit",
    defaultValues: {
      statusComment: "",
    },
  });

  useEffect(() => {
    if (mission) {
      form.reset({
        statusComment: mission.statusComment,
      });
    }
  }, [mission, form]);

  const onSubmit = useCallback(
    async (data: CloseMissionFormValues) => {
      setIsLoading(true);
      try {
        if (!mission) {
          throw new Error("Mission not found");
        }
        await closeMission({
          variables: {
            missionId: mission.id,
            statusComment: data.statusComment,
          },
        });
        await refetch();
        toast({
          description: t("form.closeMission.success"),
        });
      } catch (error) {
        console.error("useCloseMission::error", error);
        toast({
          description: t("form.closeMission.fail"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
        setOpen(false);
        setMissionToEdit(null);
      }
    },
    [closeMission, mission, setOpen, setMissionToEdit, refetch, toast, t]
  );

  return { form, isLoading: isLoading || loading, onSubmit };
};
